import React, { useState, useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import { api, getErrorMessage } from '../../lib/api';
import { toast } from '../../utils/toast';
import type { Product } from '../../types';
import { Check, Info, AlertTriangle, ArrowLeft, ArrowRight, Save } from 'lucide-react';

interface ProductWizardProps {
  product: Product;
  onUpdate: () => void;
}

type Step = 'basic' | 'authentication' | 'external-api' | 'advanced';

export const ProductWizard: React.FC<ProductWizardProps> = ({ product, onUpdate }) => {
  const [currentStep, setCurrentStep] = useState<Step>('basic');
  const [formData, setFormData] = useState({
    name: product.name || '',
    description: product.description || '',
    authentication_provider: product.authentication_provider || 'BASIC_AUTH',
    external_url_sandbox: product.external_url_sandbox || '',
    external_url_production: product.external_url_production || '',
    timeout_ms: product.timeout_ms || 30000,
    retry_count: product.retry_count || 3,
    circuit_breaker_enabled: product.circuit_breaker_enabled ?? true,
    circuit_breaker_threshold: product.circuit_breaker_threshold || 5,
    circuit_breaker_reset_timeout_ms: product.circuit_breaker_reset_timeout_ms || 30000,
    requires_external_auth: product.requires_external_auth || false,
    external_auth_type: product.external_auth_type || 'Basic Auth',
    external_auth_config: product.external_auth_config
      ? JSON.stringify(product.external_auth_config, null, 2)
      : '',
  });

  useEffect(() => {
    setFormData({
      name: product.name || '',
      description: product.description || '',
      authentication_provider: product.authentication_provider || 'BASIC_AUTH',
      external_url_sandbox: product.external_url_sandbox || '',
      external_url_production: product.external_url_production || '',
      timeout_ms: product.timeout_ms || 30000,
      retry_count: product.retry_count || 3,
      circuit_breaker_enabled: product.circuit_breaker_enabled ?? true,
      circuit_breaker_threshold: product.circuit_breaker_threshold || 5,
      circuit_breaker_reset_timeout_ms: product.circuit_breaker_reset_timeout_ms || 30000,
      requires_external_auth: product.requires_external_auth || false,
      external_auth_type: product.external_auth_type || 'Basic Auth',
      external_auth_config: product.external_auth_config
        ? JSON.stringify(product.external_auth_config, null, 2)
        : '',
    });
  }, [product]);

  const updateMutation = useMutation({
    mutationFn: async (data: Partial<Product> & { external_auth_config?: string }) => {
      const payload: any = { ...data };
      if (payload.external_auth_config) {
        try {
          payload.external_auth_config = JSON.parse(payload.external_auth_config);
        } catch {
          payload.external_auth_config = null;
        }
      } else {
        payload.external_auth_config = null;
      }
      const response = await api.put(`/portal/admin/products/${product.id}`, payload);
      return response.data;
    },
    onSuccess: () => {
      onUpdate();
      toast.success('Configuração do produto atualizada com sucesso!');
    },
    onError: (error) => {
      toast.error(`Falha ao atualizar: ${getErrorMessage(error)}`);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateMutation.mutate(formData as any);
  };

  const steps: { key: Step; label: string; description: string }[] = [
    { key: 'basic', label: 'Informação Básica', description: 'Nome e descrição do produto' },
    {
      key: 'authentication',
      label: 'Autenticação',
      description: 'Método de autenticação e upstream',
    },
    {
      key: 'external-api',
      label: 'API Externa (URLs)',
      description: 'URLs de Sandbox e Produção',
    },
    {
      key: 'advanced',
      label: 'Definições Avançadas',
      description: 'Timeout, retentativas e circuit breaker',
    },
  ];

  const currentStepIndex = steps.findIndex((s) => s.key === currentStep);

  return (
    <div className="space-y-6">
      {/* Modern Step Selector Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {steps.map((step, index) => {
          const isCurrent = currentStep === step.key;
          const isCompleted = index < currentStepIndex;
          return (
            <button
              key={step.key}
              type="button"
              onClick={() => setCurrentStep(step.key)}
              className={`p-4 rounded-2xl border text-left transition-all flex items-start gap-3.5 ${
                isCurrent
                  ? 'bg-white dark:bg-slate-900 border-primary-500 ring-2 ring-primary-500/15 shadow-xs'
                  : 'bg-white/70 dark:bg-slate-900/60 border-slate-200 dark:border-slate-800 hover:border-slate-300 dark:hover:border-slate-700'
              }`}
            >
              <div
                className={`w-8 h-8 rounded-xl flex items-center justify-center font-bold text-xs shrink-0 transition-colors ${
                  isCurrent
                    ? 'bg-primary-600 text-white shadow-xs'
                    : isCompleted
                      ? 'bg-emerald-500 text-white'
                      : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400'
                }`}
              >
                {isCompleted ? <Check className="w-4 h-4" /> : index + 1}
              </div>
              <div className="min-w-0">
                <p
                  className={`text-sm font-bold truncate ${
                    isCurrent
                      ? 'text-primary-600 dark:text-primary-400'
                      : 'text-slate-900 dark:text-white'
                  }`}
                >
                  {step.label}
                </p>
                <p className="text-xs text-slate-500 dark:text-slate-400 line-clamp-1 mt-0.5">
                  {step.description}
                </p>
              </div>
            </button>
          );
        })}
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="card">
        {updateMutation.isError && (
          <div className="mb-4 rounded-xl bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-900/50 p-4">
            <p className="text-sm text-rose-800 dark:text-rose-300">
              {getErrorMessage(updateMutation.error)}
            </p>
          </div>
        )}

        {/* Step 1: Basic Info */}
        {currentStep === 'basic' && (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
              Informação Básica
            </h3>

            <div>
              <label htmlFor="name" className="label">
                Nome do Produto *
              </label>
              <input
                id="name"
                type="text"
                required
                className="input"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>

            <div>
              <label htmlFor="description" className="label">
                Descrição *
              </label>
              <textarea
                id="description"
                required
                className="textarea h-32"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </div>
          </div>
        )}

        {/* Step 2: Authentication */}
        {currentStep === 'authentication' && (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
              Configuração de Autenticação
            </h3>

            <div>
              <label htmlFor="auth_provider" className="label">
                Fornecedor de Autenticação *
              </label>
              <select
                id="auth_provider"
                required
                className="select"
                value={formData.authentication_provider}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    authentication_provider: e.target.value as any,
                  })
                }
              >
                <option value="BASIC_AUTH">Basic Authentication</option>
                <option value="OAUTH2">OAuth 2.0</option>
                <option value="API_KEY">API Key</option>
                <option value="HMAC">Assinatura HMAC</option>
                <option value="CUSTOM">Autenticação Customizada</option>
              </select>
            </div>

            <hr className="my-6 border-slate-200 dark:border-slate-800" />
            <h4 className="text-md font-medium text-slate-900 dark:text-white mb-2">
              Autenticação Externa (Upstream)
            </h4>

            <div className="flex items-center">
              <input
                id="requires_external"
                type="checkbox"
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-slate-300 dark:border-slate-700 rounded"
                checked={formData.requires_external_auth}
                onChange={(e) =>
                  setFormData({ ...formData, requires_external_auth: e.target.checked })
                }
              />
              <label
                htmlFor="requires_external"
                className="ml-2 block text-sm text-slate-800 dark:text-slate-200 font-medium"
              >
                Requer Credenciais Externas (Aplicações fornecem os seus dados para o upstream)
              </label>
            </div>

            {formData.requires_external_auth && (
              <div className="pl-6 space-y-4 border-l-2 border-primary-200 dark:border-primary-800 mt-4">
                <div>
                  <label className="label">Tipo de Autenticação Externa</label>
                  <select
                    className="select"
                    value={formData.external_auth_type}
                    onChange={(e) =>
                      setFormData({ ...formData, external_auth_type: e.target.value })
                    }
                  >
                    <option value="Basic Auth">Basic Auth</option>
                    <option value="Bearer Token">Bearer Token</option>
                    <option value="API Key">API Key</option>
                    <option value="OAuth 2.0">OAuth 2.0</option>
                  </select>
                </div>
                <div>
                  <label className="label">Configuração (JSON)</label>
                  <textarea
                    className="textarea h-32 font-mono text-sm"
                    placeholder='{"fields": ["username", "password"]}'
                    value={formData.external_auth_config}
                    onChange={(e) =>
                      setFormData({ ...formData, external_auth_config: e.target.value })
                    }
                  />
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                    Esquema JSON que define os campos que o developer terá de preencher no portal.
                  </p>
                </div>
              </div>
            )}

            {/* Provider-specific configuration hints */}
            <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800/50 rounded-xl p-4">
              <h4 className="text-sm font-semibold text-blue-900 dark:text-blue-200 mb-2">
                Como funciona ({formData.authentication_provider})
              </h4>
              <div className="text-sm text-blue-800 dark:text-blue-300 space-y-1">
                {formData.authentication_provider === 'BASIC_AUTH' && (
                  <p>
                    As credenciais são guardadas por subscrição/aplicação. Os clientes configuram
                    utilizador e palavra-passe nas credenciais da aplicação.
                  </p>
                )}
                {formData.authentication_provider === 'OAUTH2' && (
                  <p>
                    Configure o URL do token, scope e grant type nas definições de subscrição. Os
                    tokens são renovados automaticamente pelo Integration Engine.
                  </p>
                )}
                {formData.authentication_provider === 'API_KEY' && (
                  <p>
                    Os clientes utilizam a API Key gerada no portal para autenticar as requisições
                    no Kong Gateway.
                  </p>
                )}
                {formData.authentication_provider === 'HMAC' && (
                  <p>
                    Configure o algoritmo de assinatura e cabeçalhos. A chave secreta é gerida de
                    forma encriptada por aplicação.
                  </p>
                )}
                {formData.authentication_provider === 'CUSTOM' && (
                  <p>
                    Lógica de autenticação customizada executada pelo serviço Integration Engine.
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Step 3: External API */}
        {currentStep === 'external-api' && (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
              URLs da API Externa (Sandbox & Produção)
            </h3>

            <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800/50 rounded-xl p-4 text-sm text-blue-800 dark:text-blue-200">
              <p className="font-semibold mb-1 flex items-center gap-1.5">
                <Info className="w-4 h-4" />
                <span>Como estas URLs são usadas:</span>
              </p>
              <ul className="list-disc list-inside space-y-1">
                <li>
                  O Kong Gateway e o Integration Engine encaminham as chamadas para estas URLs
                </li>
                <li>
                  Ao carregar uma especificação OpenAPI com bloco <code>servers</code>, estas URLs
                  são preenchidas automaticamente se estiverem vazias
                </li>
                <li>Sandbox URL → usada quando o cliente testa em modo Sandbox</li>
                <li>Production URL → usada quando o cliente opera em modo Produção</li>
              </ul>
            </div>

            <div>
              <label htmlFor="sandbox_url" className="label">
                URL Base de Sandbox
              </label>
              <input
                id="sandbox_url"
                type="url"
                placeholder="https://sandbox-api.example.com/v1"
                className="input"
                value={formData.external_url_sandbox}
                onChange={(e) => setFormData({ ...formData, external_url_sandbox: e.target.value })}
              />
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                URL base do servidor de homologação/teste (ex:
                https://ekz-partnersapi-qa.e-kwanza.ao)
              </p>
            </div>

            <div>
              <label htmlFor="production_url" className="label">
                URL Base de Produção
              </label>
              <input
                id="production_url"
                type="url"
                placeholder="https://api.example.com/v1"
                className="input"
                value={formData.external_url_production}
                onChange={(e) =>
                  setFormData({ ...formData, external_url_production: e.target.value })
                }
              />
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                URL base do servidor de produção (ex: https://ekz-partnersapi.e-kwanza.ao)
              </p>
            </div>

            <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800/50 rounded-xl p-4 text-sm text-amber-800 dark:text-amber-200">
              <p className="font-semibold mb-1 flex items-center gap-1.5">
                <AlertTriangle className="w-4 h-4" />
                <span>Nota sobre a spec OpenAPI:</span>
              </p>
              <p>
                No Developer Portal, o Swagger UI aponta automaticamente para o endereço público do
                Kong Gateway consoante o ambiente ativo do cliente (Sandbox ou Produção), mantendo
                os servidores internos protegidos.
              </p>
            </div>
          </div>
        )}

        {/* Step 4: Advanced Settings */}
        {currentStep === 'advanced' && (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
              Definições Avançadas de Resiliência
            </h3>

            {/* Timeout & Retry */}
            <div className="space-y-4">
              <h4 className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                Timeout e Retentativas
              </h4>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="timeout" className="label">
                    Timeout (ms)
                  </label>
                  <input
                    id="timeout"
                    type="number"
                    min="1000"
                    max="300000"
                    step="1000"
                    className="input"
                    value={formData.timeout_ms}
                    onChange={(e) =>
                      setFormData({ ...formData, timeout_ms: parseInt(e.target.value) || 30000 })
                    }
                  />
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                    Padrão: 30000ms (30s)
                  </p>
                </div>

                <div>
                  <label htmlFor="retry" className="label">
                    Número de Retentativas (Retries)
                  </label>
                  <input
                    id="retry"
                    type="number"
                    min="0"
                    max="10"
                    className="input"
                    value={formData.retry_count}
                    onChange={(e) =>
                      setFormData({ ...formData, retry_count: parseInt(e.target.value) || 0 })
                    }
                  />
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                    Padrão: 3 tentativas
                  </p>
                </div>
              </div>
            </div>

            {/* Circuit Breaker */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                  Circuit Breaker
                </h4>
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.circuit_breaker_enabled}
                    onChange={(e) =>
                      setFormData({ ...formData, circuit_breaker_enabled: e.target.checked })
                    }
                    className="mr-2 rounded border-slate-300 dark:border-slate-700"
                  />
                  <span className="text-sm text-slate-600 dark:text-slate-300">Ativo</span>
                </label>
              </div>

              {formData.circuit_breaker_enabled && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="cb_threshold" className="label">
                      Limite de Falhas (Threshold)
                    </label>
                    <input
                      id="cb_threshold"
                      type="number"
                      min="1"
                      max="100"
                      className="input"
                      value={formData.circuit_breaker_threshold}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          circuit_breaker_threshold: parseInt(e.target.value) || 5,
                        })
                      }
                    />
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                      Falhas consecutivas antes de abrir o circuito
                    </p>
                  </div>

                  <div>
                    <label htmlFor="cb_reset" className="label">
                      Tempo de Reset (ms)
                    </label>
                    <input
                      id="cb_reset"
                      type="number"
                      min="1000"
                      max="300000"
                      step="1000"
                      className="input"
                      value={formData.circuit_breaker_reset_timeout_ms}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          circuit_breaker_reset_timeout_ms: parseInt(e.target.value) || 30000,
                        })
                      }
                    />
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                      Tempo de espera antes de testar novamente o upstream
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Navigation & Save Buttons */}
        <div className="flex flex-wrap items-center justify-between gap-3 pt-6 mt-6 border-t border-slate-200 dark:border-slate-800">
          <button
            type="button"
            onClick={() => {
              const prevIndex = Math.max(0, currentStepIndex - 1);
              setCurrentStep(steps[prevIndex].key);
            }}
            disabled={currentStepIndex === 0}
            className="btn-secondary inline-flex items-center gap-1.5 disabled:opacity-40"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Anterior</span>
          </button>

          <div className="flex items-center gap-2">
            {currentStepIndex < steps.length - 1 && (
              <button
                type="button"
                onClick={() => {
                  const nextIndex = Math.min(steps.length - 1, currentStepIndex + 1);
                  setCurrentStep(steps[nextIndex].key);
                }}
                className="btn-secondary inline-flex items-center gap-1.5"
              >
                <span>Seguinte</span>
                <ArrowRight className="w-4 h-4" />
              </button>
            )}
            <button
              type="submit"
              disabled={updateMutation.isPending}
              className="btn-primary inline-flex items-center gap-1.5"
            >
              <Save className="w-4 h-4" />
              <span>{updateMutation.isPending ? 'A guardar...' : 'Guardar Configuração'}</span>
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};
